import type { Difficulty } from "./match.ts";

/**
 * Jangada e Boitatá (doc 01: Jangada, Reparo, Embarque, Encontro final, Vitória e derrota; doc 07:
 * I8, L4, L5). Hipóteses de balanceamento, centralizadas aqui.
 */
export const RAFT_PARTS = ["casco", "mastro", "vela", "leme", "altar"] as const;
export type RaftPartId = (typeof RAFT_PARTS)[number];

export interface RaftStage {
  /** Itens base para 2 jogadores no Arretado. */
  items: Readonly<Record<string, number>>;
  /** Não escala com jogadores nem dificuldade (Pedra-de-raio, Muiraquitãs). */
  fixed?: boolean;
}

export interface RaftPartDef {
  id: RaftPartId;
  name: string;
  stages: readonly RaftStage[];
}

/** Componentes com etapas em sequência (doc 01, tabela); o Altar vem dos Muiraquitãs do Relicário. */
export const RAFT_PART_DEFS: Record<RaftPartId, RaftPartDef> = {
  casco: { id: "casco", name: "Casco", stages: [{ items: { madeira: 60 } }, { items: { pedra: 30, "lingote-ferro": 10 } }, { items: { "lingote-prata": 8 } }] },
  mastro: { id: "mastro", name: "Mastro", stages: [{ items: { madeira: 40 } }, { items: { "lingote-ferro": 8 } }] },
  vela: { id: "vela", name: "Vela", stages: [{ items: { couro: 18, madeira: 10 } }] },
  leme: { id: "leme", name: "Leme", stages: [{ items: { madeira: 25, "lingote-ferro": 6 } }, { items: { "fragmento-raio": 2 }, fixed: true }] },
  altar: { id: "altar", name: "Altar das Gemas", stages: [{ items: { muiraquita: 5 }, fixed: true }] },
};

/** k_dif do custo da Jangada (doc 01). */
export const RAFT_DIFFICULTY: Record<Difficulty, number> = { tranquilo: 0.85, arretado: 1, sinistro: 1.15 };

/** Custo de um item da etapa: ⌈base · (0,7 + 0,15n) · k_dif⌉; fixos não escalam. n é fixado no início. */
export function raftCost(base: number, players: number, difficulty: Difficulty, fixed = false): number {
  if (fixed) return base;
  return Math.ceil(base * (0.7 + 0.15 * Math.max(1, players)) * RAFT_DIFFICULTY[difficulty]);
}

/** Itens de uma etapa já escalados. */
export function stageCost(part: RaftPartId, stage: number, players: number, difficulty: Difficulty): Record<string, number> {
  const s = RAFT_PART_DEFS[part].stages[stage];
  if (!s) return {};
  return Object.fromEntries(Object.entries(s.items).map(([item, qty]) => [item, raftCost(qty, players, difficulty, s.fixed)]));
}

export const RAFT = {
  /** Descoberta a 35 m dos destroços (ou pelo Mapa da Jangada). */
  discover: 35,
  /** Depósito a até 6 m do centro dos destroços. */
  reach: 6,
  /** Convés: retângulo de 12 × 6 m no rumo da travessia, centrado nos destroços. */
  deck: { length: 12, width: 6 },
  /** Contagem do Zarpar (cancelada se alguém sai do convés) e a travessia: 20 s a 3 m/s. */
  countdown: 10,
  travel: 20,
  speed: 3,
  /** Mortos renascem no convés com 60 % da Vida (doc 07, L4). */
  reviveHp: 0.6,
  /** Altura do convés acima do mar, ancorado na Boca. */
  deckHeight: 0.6,
  /** Anel de fogo da Boca: raio e dano por segundo fora dele (fração da Vida máxima). */
  ring: 90,
  ringDamage: 0.08,
  /** Cena da vitória antes do resultado (s). */
  victory: 6,
} as const;

/** Id do colisor do convés ancorado (acima dos de recursos, POIs e estruturas). */
export const RAFT_DECK_COLLIDER = 1_879_048_192;

/** Piso do convés ancorado na Boca (alinhado ao eixo z): caixa igual no servidor e no cliente. */
export function anchoredDeckBox(x: number, z: number, seaLevel: number): { minX: number; maxX: number; minY: number; maxY: number; minZ: number; maxZ: number } {
  const hw = RAFT.deck.width / 2;
  const hl = RAFT.deck.length / 2;
  return { minX: x - hw, maxX: x + hw, minY: seaLevel - 1, maxY: seaLevel + RAFT.deckHeight, minZ: z - hl, maxZ: z + hl };
}

/** Proa do convés ancorado (onde o Boitatá se enrosca). */
export const anchoredBow = (x: number, z: number): { x: number; z: number } => ({ x, z: z + RAFT.deck.length / 2 });

/** Boitatá (doc 01): vida, dano, abertura, Mergulho e Abraço. */
export const BOITATA = {
  difficultyHp: { tranquilo: 0.85, arretado: 1, sinistro: 1.2 } as Record<Difficulty, number>,
  difficultyDamage: { tranquilo: 0.8, arretado: 1, sinistro: 1.25 } as Record<Difficulty, number>,
  /** Rugido de abertura: sem atacar nem apanhar. */
  opening: 6,
  /** Mergulho: 8 pontos num anel de 45–70 m, a 8–25 m de altura, por 20–35 s (12–20 s na fase 3). */
  dive: { points: 8, ring: [45, 70] as const, altitude: [8, 25] as const, seconds: [20, 35] as const, secondsF3: [12, 20] as const },
  /** Cuspe: a cada 3,0 − 1,2 · (1 − vida%) s. */
  spit: { base: 3, scale: 1.2 },
  /** Abraço: pouso telegrafado 3 s, espera 2 s, 5–8 ataques (6–9 na fase 3) com 0,5–1,5 s entre eles; sai ao perder 20 % no mesmo Abraço. */
  coil: { telegraph: 3, wait: 2, attacks: [5, 8] as const, attacksF3: [6, 9] as const, gap: [0.5, 1.5] as const, breakAt: 0.2 },
  /** Transição de fase: 3 s invulnerável, rugido e empurrão radial de 6 m que limpa o fogo do convés. */
  transition: { seconds: 3, push: 6 },
} as const;

/** Vida do Boitatá: 4000 · (0,6 + 0,3n) · (1 + 0,15d) · k_dif; dano · (1 + 0,08d) · k_dano. */
export function boitataMods(players: number, day: number, difficulty: Difficulty): { hpMul: number; damageMul: number } {
  return {
    hpMul: (0.6 + 0.3 * Math.max(1, players)) * (1 + 0.15 * Math.max(0, day)) * BOITATA.difficultyHp[difficulty],
    damageMul: (1 + 0.08 * Math.max(0, day)) * BOITATA.difficultyDamage[difficulty],
  };
}

/** Intervalo do cuspe pela vida que sobra (1 = cheio): de 3,0 a 1,8 s. */
export const spitInterval = (hpFraction: number): number => BOITATA.spit.base - BOITATA.spit.scale * (1 - Math.max(0, Math.min(1, hpFraction)));
